import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { AlertTriangle } from 'lucide-react';
import { Modal } from './Modal';

export interface ConfirmOptions {
    title: string;
    message: ReactNode;
    confirmLabel?: string;
    cancelLabel?: string;
    /** Red confirm button for destructive actions. */
    danger?: boolean;
}

type ConfirmFn = (options: ConfirmOptions) => Promise<boolean>;

const ConfirmContext = createContext<ConfirmFn | undefined>(undefined);

export function ConfirmProvider({ children }: { children: ReactNode }) {
    const [options, setOptions] = useState<ConfirmOptions | null>(null);
    const resolver = useRef<((v: boolean) => void) | null>(null);

    const confirm = useCallback<ConfirmFn>((opts) => {
        return new Promise<boolean>((resolve) => {
            resolver.current = resolve;
            setOptions(opts);
        });
    }, []);

    const finish = useCallback((value: boolean) => {
        resolver.current?.(value);
        resolver.current = null;
        setOptions(null);
    }, []);

    const value = useMemo(() => confirm, [confirm]);

    return (
        <ConfirmContext.Provider value={value}>
            {children}
            <Modal
                open={!!options}
                onClose={() => finish(false)}
                title={options?.title}
                footer={options && (
                    <>
                        <button type="button" className="btn btn-secondary" onClick={() => finish(false)}>
                            {options.cancelLabel ?? 'Cancel'}
                        </button>
                        <button
                            type="button"
                            className={`btn ${options.danger ? 'btn-danger' : 'btn-primary'}`}
                            onClick={() => finish(true)}
                            autoFocus
                        >
                            {options.confirmLabel ?? 'Confirm'}
                        </button>
                    </>
                )}
            >
                {options && (
                    <div className="flex gap-4 items-center">
                        {options.danger && <AlertTriangle size={28} color="var(--ft-error)" style={{ flexShrink: 0 }} />}
                        <div>{typeof options.message === 'string' ? <p>{options.message}</p> : options.message}</div>
                    </div>
                )}
            </Modal>
        </ConfirmContext.Provider>
    );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useConfirm(): ConfirmFn {
    const ctx = useContext(ConfirmContext);
    if (!ctx) throw new Error('useConfirm must be used within ConfirmProvider');
    return ctx;
}
